'use client';

import React, { useState, useMemo } from 'react';
import {
    Library,
    Search,
    Filter,
    Calendar,
    ChevronDown,
    Eye,
    Printer,
    FileDown,
    ArrowUpRight,
    ArrowDownRight,
    Wallet,
    TrendingUp,
    TrendingDown,
    Calculator,
    ArrowLeft
} from 'lucide-react';
import Link from 'next/link';

// --- Types ---

interface LedgerEntry {
    id: string;
    date: string;
    journalId: string;
    description: string;
    debit: number;
    credit: number;
}

interface Account {
    code: string;
    name: string;
    normalBalance: 'debit' | 'credit';
    initialBalance: number;
}

// --- Mock Data ---

const MOCK_ACCOUNTS: Account[] = [
    { code: '1-100', name: 'Kas Tunai', normalBalance: 'debit', initialBalance: 1500000 },
    { code: '1-101', name: 'Bank BSI', normalBalance: 'debit', initialBalance: 25000000 },
    { code: '1-102', name: 'Bank Mandiri', normalBalance: 'debit', initialBalance: 50000000 },
    { code: '4-101', name: 'Pendapatan SPP', normalBalance: 'credit', initialBalance: 0 },
    { code: '5-101', name: 'Beban ATK', normalBalance: 'debit', initialBalance: 0 },
];

const MOCK_ENTRIES: Record<string, LedgerEntry[]> = {
    '1-100': [
        { id: 'LE-001', date: '2024-02-01', journalId: 'JRN-2024-001', description: 'Saldo Awal', debit: 0, credit: 0 },
        { id: 'LE-002', date: '2024-02-10', journalId: 'JRN-2024-001', description: 'Pembelian Kertas A4 & Tinta Printer', debit: 0, credit: 450000 },
        { id: 'LE-003', date: '2024-02-15', journalId: 'JRN-2024-008', description: 'Penarikan Kas untuk Petty Cash', debit: 1000000, credit: 0 },
        { id: 'LE-004', date: '2024-02-20', journalId: 'JRN-2024-012', description: 'Pembayaran Konsumsi Rapat Gaji', debit: 0, credit: 200000 },
    ],
    '1-101': [
        { id: 'LE-010', date: '2024-02-09', journalId: 'JRN-2024-002', description: 'Penerimaan SPP - Budi Santoso', debit: 500000, credit: 0 },
        { id: 'LE-011', date: '2024-02-12', journalId: 'JRN-2024-005', description: 'Penerimaan SPP - Siti Khadijah', debit: 500000, credit: 0 },
    ]
};

export default function LaporanBukuBesarPage() {
    // --- State ---
    const [selectedAccountCode, setSelectedAccountCode] = useState<string>(MOCK_ACCOUNTS[0].code);
    const [dateRange, setDateRange] = useState('01 Feb 2024 - 29 Feb 2024');
    const [typeFilter, setTypeFilter] = useState<'all' | 'debit' | 'credit'>('all');

    // --- Memoized Calculations ---
    const selectedAccount = useMemo(() =>
        MOCK_ACCOUNTS.find(a => a.code === selectedAccountCode) || MOCK_ACCOUNTS[0],
        [selectedAccountCode]);

    const mutations = useMemo(() => {
        const entries = MOCK_ENTRIES[selectedAccountCode] || [];
        let currentBalance = selectedAccount.initialBalance;

        return entries.map(entry => {
            if (selectedAccount.normalBalance === 'debit') {
                currentBalance = currentBalance + entry.debit - entry.credit;
            } else {
                currentBalance = currentBalance - entry.debit + entry.credit;
            }
            return { ...entry, runningBalance: currentBalance };
        }).filter(item => {
            if (typeFilter === 'debit') return item.debit > 0;
            if (typeFilter === 'credit') return item.credit > 0;
            return true;
        });
    }, [selectedAccountCode, selectedAccount, typeFilter]);

    const summary = useMemo(() => {
        const allEntries = MOCK_ENTRIES[selectedAccountCode] || [];
        const totalDebit = allEntries.reduce((acc, curr) => acc + curr.debit, 0);
        const totalCredit = allEntries.reduce((acc, curr) => acc + curr.credit, 0);

        let finalBalance = selectedAccount.initialBalance;
        if (selectedAccount.normalBalance === 'debit') {
            finalBalance = finalBalance + totalDebit - totalCredit;
        } else {
            finalBalance = finalBalance - totalDebit + totalCredit;
        }

        return {
            initial: selectedAccount.initialBalance,
            debit: totalDebit,
            credit: totalCredit,
            final: finalBalance
        };
    }, [selectedAccountCode, selectedAccount]);

    // --- Helpers ---
    const formatCurrency = (amount: number) => {
        return new Intl.NumberFormat('id-ID', {
            style: 'currency',
            currency: 'IDR',
            minimumFractionDigits: 0
        }).format(amount);
    };

    return (
        <main className="p-4 md:p-10 space-y-8 max-w-7xl mx-auto w-full pb-32 animate-in fade-in duration-700">
            {/* Header */}
            <section className="bg-surface p-8 rounded-4xl border border-border-main shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 bg-primary-soft/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>
                <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
                    <div className="space-y-4">
                        <div className="flex items-center gap-2 text-[10px] font-bold text-muted uppercase tracking-widest">
                            <Link href="/sekolah" className="hover:text-primary transition-all">Laporan</Link>
                            <span className="text-muted/50">•</span>
                            <span className="text-foreground">Buku Besar</span>
                        </div>
                        <div className="flex items-center gap-6">
                            <div className="w-16 h-16 rounded-2xl bg-secondary-soft/50 border border-secondary/10 flex items-center justify-center text-secondary shadow-inner">
                                <Library className="w-8 h-8" />
                            </div>
                            <div className="space-y-1">
                                <h1 className="text-3xl md:text-4xl font-black text-foreground tracking-tight whitespace-nowrap">Buku Besar</h1>
                                <p className="text-secondary font-medium flex items-center gap-2 text-sm uppercase tracking-wider">
                                    <Calculator className="w-3.5 h-3.5 text-primary" /> Detail Mutasi & Saldo Akun • PAUD Al-Azhar 1
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-3">
                        <button className="flex items-center gap-3 px-6 py-3.5 bg-background border border-border-main rounded-2xl text-[10px] font-black uppercase tracking-widest hover:border-primary/30 transition-all shadow-sm group">
                            <FileDown className="w-4 h-4 text-primary group-hover:scale-110 transition-transform" /> Export Excel
                        </button>
                        <button className="flex items-center gap-3 px-6 py-3.5 bg-primary text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl shadow-primary/20 hover:scale-[1.02] transition-all">
                            <Printer className="w-4 h-4" /> Export PDF
                        </button>
                    </div>
                </div>
            </section>

            {/* Filter Section */}
            <div className="bg-surface border border-border-main p-6 rounded-3xl shadow-sm space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-end">
                    {/* Account Selector */}
                    <div className="md:col-span-5 space-y-2">
                        <label className="text-[10px] font-black text-muted uppercase tracking-tighter ml-1">Pilih Akun (COA)</label>
                        <div className="relative group">
                            <Wallet className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted group-focus-within:text-primary transition-colors" />
                            <select
                                value={selectedAccountCode}
                                onChange={(e) => setSelectedAccountCode(e.target.value)}
                                className="w-full pl-12 pr-10 py-4 bg-background border border-border-main rounded-2xl text-sm font-bold text-foreground outline-none appearance-none cursor-pointer hover:border-primary/30 focus:ring-4 focus:ring-primary/5 transition-all"
                            >
                                {MOCK_ACCOUNTS.map(acc => (
                                    <option key={acc.code} value={acc.code}>{acc.code} - {acc.name}</option>
                                ))}
                            </select>
                            <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted pointer-events-none" />
                        </div>
                    </div>

                    {/* Date Range */}
                    <div className="md:col-span-4 space-y-2">
                        <label className="text-[10px] font-black text-muted uppercase tracking-tighter ml-1">Rentang Tanggal</label>
                        <button className="w-full flex items-center justify-between px-5 py-4 bg-background border border-border-main rounded-2xl text-xs font-bold text-muted hover:border-primary/30 transition-all shadow-sm">
                            <div className="flex items-center gap-3">
                                <Calendar className="w-4 h-4 text-primary" />
                                {dateRange}
                            </div>
                            <ChevronDown className="w-4 h-4" />
                        </button>
                    </div>

                    {/* Mutation Type */}
                    <div className="md:col-span-3 space-y-2">
                        <label className="text-[10px] font-black text-muted uppercase tracking-tighter ml-1">Tampilkan Transaksi</label>
                        <div className="flex bg-background p-1.5 rounded-2xl border border-border-main gap-1">
                            <button
                                onClick={() => setTypeFilter('all')}
                                className={`flex-1 py-2 text-[9px] font-black uppercase tracking-tighter rounded-xl transition-all ${typeFilter === 'all' ? 'bg-primary text-white shadow-lg shadow-primary/20' : 'text-muted hover:text-foreground'}`}
                            >
                                Semua
                            </button>
                            <button
                                onClick={() => setTypeFilter('debit')}
                                className={`flex-1 py-2 text-[9px] font-black uppercase tracking-tighter rounded-xl transition-all ${typeFilter === 'debit' ? 'bg-primary text-white shadow-lg shadow-primary/20' : 'text-muted hover:text-foreground'}`}
                            >
                                Debit
                            </button>
                            <button
                                onClick={() => setTypeFilter('credit')}
                                className={`flex-1 py-2 text-[9px] font-black uppercase tracking-tighter rounded-xl transition-all ${typeFilter === 'credit' ? 'bg-primary text-white shadow-lg shadow-primary/20' : 'text-muted hover:text-foreground'}`}
                            >
                                Kredit
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="p-6 rounded-3xl bg-background border border-border-main shadow-sm flex flex-col gap-4">
                    <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold text-muted uppercase tracking-widest">Saldo Awal</span>
                        <div className="p-2 bg-secondary-soft/30 rounded-lg text-secondary"><Wallet className="w-4 h-4" /></div>
                    </div>
                    <p className="text-2xl font-black text-foreground">{formatCurrency(summary.initial)}</p>
                    <p className="text-[10px] text-muted font-medium uppercase tracking-tighter">Per 01 Feb 2024</p>
                </div>
                <div className="p-6 rounded-3xl bg-background border-l-4 border-l-success border border-border-main shadow-sm flex flex-col gap-4">
                    <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold text-muted uppercase tracking-widest">Total Debit</span>
                        <div className="p-2 bg-success-soft/30 rounded-lg text-success"><TrendingUp className="w-4 h-4" /></div>
                    </div>
                    <p className="text-2xl font-black text-success">{formatCurrency(summary.debit)}</p>
                    <p className="text-[10px] text-muted font-medium uppercase tracking-tighter">Mutasi Penambahan</p>
                </div>
                <div className="p-6 rounded-3xl bg-background border-l-4 border-l-error border border-border-main shadow-sm flex flex-col gap-4">
                    <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold text-muted uppercase tracking-widest">Total Kredit</span>
                        <div className="p-2 bg-error-soft/30 rounded-lg text-error"><TrendingDown className="w-4 h-4" /></div>
                    </div>
                    <p className="text-2xl font-black text-error">{formatCurrency(summary.credit)}</p>
                    <p className="text-[10px] text-muted font-medium uppercase tracking-tighter">Mutasi Pengurangan</p>
                </div>
                <div className="p-6 rounded-3xl bg-primary text-white shadow-xl shadow-primary/20 flex flex-col gap-4">
                    <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold text-primary-soft uppercase tracking-widest">Saldo Akhir</span>
                        <div className="p-2 bg-white/20 rounded-lg text-white"><ArrowUpRight className="w-4 h-4" /></div>
                    </div>
                    <p className="text-2xl font-black">{formatCurrency(summary.final)}</p>
                    <p className="text-[10px] text-primary-soft font-medium uppercase tracking-tighter">Per 29 Feb 2024</p>
                </div>
            </div>

            {/* Mutations Table */}
            <div className="bg-surface border border-border-main rounded-3xl overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-background/50 border-b border-border-main">
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest w-[140px]">Tanggal</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest w-[140px]">No. Jurnal</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">Keterangan Transaksi</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest w-[150px] text-right">Debit</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest w-[150px] text-right">Kredit</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest w-[180px] text-right">Saldo Berjalan</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest w-[100px] text-center">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border-main/50">
                            {/* Static Initial Balance Row */}
                            <tr className="bg-muted/5">
                                <td className="px-6 py-4 text-[11px] font-bold text-muted uppercase">01/02/2024</td>
                                <td className="px-6 py-4">-</td>
                                <td className="px-6 py-4 text-[11px] font-black text-muted uppercase italic">Saldo Awal Periode</td>
                                <td className="px-6 py-4"></td>
                                <td className="px-6 py-4"></td>
                                <td className="px-6 py-4 text-right font-black text-muted tabular-nums">{formatCurrency(summary.initial)}</td>
                                <td className="px-6 py-4"></td>
                            </tr>

                            {mutations.length > 0 ? mutations.map((item) => (
                                <tr key={item.id} className="hover:bg-primary-soft/5 transition-colors group">
                                    <td className="px-6 py-4">
                                        <span className="text-[11px] font-bold text-foreground">{item.date}</span>
                                    </td>
                                    <td className="px-6 py-4">
                                        <Link href={`/sekolah/laporan/jurnal?search=${item.journalId}`} className="text-[11px] font-black text-primary hover:underline uppercase tracking-tighter">
                                            {item.journalId}
                                        </Link>
                                    </td>
                                    <td className="px-6 py-4">
                                        <p className="text-[11px] font-bold text-secondary line-clamp-1">{item.description}</p>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <span className={`text-[11px] font-black tabular-nums ${item.debit > 0 ? 'text-success' : 'text-muted'}`}>
                                            {item.debit > 0 ? formatCurrency(item.debit) : '-'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <span className={`text-[11px] font-black tabular-nums ${item.credit > 0 ? 'text-error' : 'text-muted'}`}>
                                            {item.credit > 0 ? formatCurrency(item.credit) : '-'}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <span className="text-xs font-black text-foreground tabular-nums">
                                            {formatCurrency(item.runningBalance)}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <Link href={`/sekolah/laporan/jurnal?search=${item.journalId}`}>
                                            <button className="p-2 rounded-xl text-muted hover:text-primary hover:bg-primary-soft transition-all" title="Lihat Jurnal">
                                                <Eye className="w-4 h-4" />
                                            </button>
                                        </Link>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={7} className="px-6 py-10 text-center text-muted font-medium italic">
                                        Tidak ada mutasi ditemukan untuk akun ini dalam periode terpilih
                                    </td>
                                </tr>
                            )}
                        </tbody>
                        <tfoot className="bg-background/50 border-t border-border-main font-black">
                            <tr>
                                <td colSpan={3} className="px-6 py-5 text-[10px] text-muted uppercase tracking-widest">Total Mutasi & Saldo Akhir</td>
                                <td className="px-6 py-5 text-right text-success text-xs tabular-nums">{formatCurrency(summary.debit)}</td>
                                <td className="px-6 py-5 text-right text-error text-xs tabular-nums">{formatCurrency(summary.credit)}</td>
                                <td className="px-6 py-5 text-right text-primary text-sm tabular-nums">{formatCurrency(summary.final)}</td>
                                <td></td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>

            {/* Quick Actions Footer */}
            <div className="flex items-center justify-between p-6 bg-surface border border-border-main rounded-3xl shadow-sm">
                <div className="flex items-center gap-4 text-muted">
                    <ArrowLeft className="w-4 h-4" />
                    <p className="text-[10px] font-bold uppercase tracking-widest">Gunakan filter di atas untuk menganalisis akun lain</p>
                </div>
                <div className="flex gap-4">
                    <button className="px-6 py-3 bg-white border border-border-main rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-secondary-soft transition-all shadow-sm flex items-center gap-2">
                        <Printer className="w-4 h-4" /> Print Laporan
                    </button>
                </div>
            </div>
        </main>
    );
}
